import { useState } from 'react'
import { FileText, MessageSquare, Users, FileCode, Settings } from 'lucide-react'
import { AuditSummaryBanner } from './audit-summary-banner'
import { ReviewActionTree } from './review-action-tree'
import { ReviewMessageTimeline } from './review-message-timeline'
import { StakeholderCards } from './stakeholder-cards'
import { ScenarioOverview } from './scenario-overview'
import { SettingsOverview } from './settings-overview'
import type { SimulationReviewResponse } from '../types'

type ReviewerContentProps = {
  simulation: SimulationReviewResponse
}

type TabId = 'actions' | 'messages' | 'stakeholders' | 'scenario' | 'settings'

const tabs: { id: TabId; label: string; icon: typeof FileText }[] = [
  { id: 'actions', label: 'Actions', icon: FileText },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'stakeholders', label: 'Stakeholders', icon: Users },
  { id: 'scenario', label: 'Scenario', icon: FileCode },
  { id: 'settings', label: 'Settings', icon: Settings },
]

export function ReviewerContent({ simulation }: ReviewerContentProps) {
  const [activeTab, setActiveTab] = useState<TabId>('actions')

  const handleFilterMissing = () => {
    setActiveTab('actions')
    // The action tree will handle the filter internally when switched to "without-measures"
  }

  return (
    <div className="flex flex-col h-full">
      {/* Audit summary banner */}
      <AuditSummaryBanner
        auditInfo={simulation.auditInfo}
        onFilterMissing={handleFilterMissing}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white">
        <nav className="flex space-x-1 px-4" aria-label="Tabs">
          {tabs.map(tab => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id

            // Add badge for counts
            let badge: number | null = null
            if (tab.id === 'actions') badge = simulation.auditInfo.totalActions
            if (tab.id === 'messages') badge = simulation.auditInfo.totalMessages
            if (tab.id === 'stakeholders') badge = simulation.auditInfo.totalStakeholders

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {badge !== null && badge > 0 && (
                  <span
                    className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                      isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {badge}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden bg-gray-50">
        {activeTab === 'actions' && (
          <ReviewActionTree actionTree={simulation.actionTree} />
        )}
        {activeTab === 'messages' && (
          <ReviewMessageTimeline messages={simulation.messages} />
        )}
        {activeTab === 'stakeholders' && (
          <StakeholderCards stakeholders={simulation.stakeholders} />
        )}
        {activeTab === 'scenario' && (
          <ScenarioOverview scenario={simulation.scenario} />
        )}
        {activeTab === 'settings' && (
          <SettingsOverview settings={simulation.settings} />
        )}
      </div>
    </div>
  )
}
